import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import AppShell from "../../components/AppShell";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { LumiMascot } from "../../components/Brand";
import { toast } from "sonner";
import { Copy, Users, TrendingUp } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip } from "recharts";

export default function ParentDashboard() {
  const { t } = useTranslation();
  const [children, setChildren] = useState([]);
  const [parentCode, setParentCode] = useState("");
  const [active, setActive] = useState(null);
  const [report, setReport] = useState(null);

  const load = useCallback(async () => {
    const [c, k] = await Promise.all([
      api.get("/parent/children"),
      api.get("/parent/code"),
    ]);
    setChildren(c.data);
    setParentCode(k.data.parent_code);
  }, []);
  useEffect(() => { load(); }, [load]);

  async function openReport(sid) {
    const r = await api.get(`/parent/report/${sid}`);
    setActive(sid);
    setReport(r.data);
  }

  return (
    <AppShell>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-adult font-bold text-3xl text-slate-900">{t("parent.title")}</h1>
          <p className="text-slate-600">{t("parent.subtitle")}</p>
        </div>
        <div className="hb-card p-4">
          <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{t("parent.yourCode")}</div>
          <div className="mt-1 flex items-center gap-2">
            <code className="text-lg font-mono font-bold text-slate-900" data-testid="parent-code">{parentCode}</code>
            <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(parentCode); toast.success(t("common.copied")); }}>
              <Copy className="w-4 h-4 mr-1" /> {t("common.copy")}
            </Button>
          </div>
          <div className="text-xs text-slate-500 mt-1">{t("parent.codeShare")}</div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4 mt-6">
        {children.length === 0 && (
          <div className="hb-card p-8 md:col-span-2 flex items-center gap-4">
            <LumiMascot size={80} />
            <div>
              <div className="font-adult font-bold text-slate-900">{t("parent.noChildren")}</div>
              <div className="text-sm text-slate-600 mt-1">{t("parent.noChildrenBody")}</div>
            </div>
          </div>
        )}
        {children.map((c) => (
          <div key={c.student.id} className="hb-card p-5" data-testid={`child-card-${c.student.id}`}>
            <div className="flex items-center justify-between">
              <div>
                <div className="font-adult font-bold text-slate-900">{c.student.profile?.display_name || c.student.full_name}</div>
                <div className="text-xs text-slate-500">{t("student.grade")} {c.student.profile?.grade_level || "—"} · {c.student.profile?.subjects?.join(", ") || ""}</div>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <span className="flex items-center gap-1"><Users className="w-4 h-4 text-blue-600"/> {c.tutor_turns}</span>
                <span className="flex items-center gap-1"><TrendingUp className="w-4 h-4 text-green-600"/> {c.scans}</span>
              </div>
            </div>
            <div className="h-40 mt-3 chart-host">
              <ResponsiveContainer>
                <BarChart data={c.confidence}>
                  <XAxis dataKey="subject" tick={{ fontSize: 9 }} />
                  <YAxis domain={[0, 5]} hide />
                  <Tooltip />
                  <Bar dataKey="confidence" fill="#3B82F6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-3 flex gap-2">
              <Button size="sm" className="rounded-full bg-slate-900 text-white hover:bg-slate-800" onClick={() => openReport(c.student.id)} data-testid={`view-report-${c.student.id}`}>{t("parent.viewReport")}</Button>
            </div>
          </div>
        ))}
      </div>

      {active && report && (
        <div className="fixed inset-0 bg-slate-900/60 z-40 flex items-center justify-center p-4" onClick={() => setActive(null)}>
          <div className="bg-white rounded-2xl max-w-3xl w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="report-modal">
            <div className="flex items-center justify-between">
              <div className="font-adult font-bold text-2xl text-slate-900">{t("parent.weeklyReport")}</div>
              <button onClick={() => setActive(null)} aria-label={t("common.close")}>×</button>
            </div>
            <div className="grid md:grid-cols-2 gap-4 mt-4">
              <div>
                <div className="font-semibold text-slate-900">{t("parent.recentScans")}</div>
                <ul className="mt-2 space-y-2 max-h-72 overflow-auto">
                  {(report.scans || []).slice(0,8).map((s) => (
                    <li key={s.id} className="p-2 border border-slate-200 rounded-lg text-sm">
                      <div className="text-xs text-slate-500">{new Date(s.created_at).toLocaleString()}</div>
                      <div><b>{t("teacher.subject")}:</b> {s.subject} · <b>{t("student.grade")}:</b> {s.grade_estimate}</div>
                      <div className="text-slate-700 text-xs">{(s.questions || []).slice(0,3).join(" · ")}</div>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <div className="font-semibold text-slate-900">{t("progress.confidenceBySubject")}</div>
                <div className="h-56 mt-2 chart-host">
                  <ResponsiveContainer>
                    <BarChart data={report.confidence}>
                      <XAxis dataKey="subject" tick={{ fontSize: 10 }} />
                      <YAxis domain={[0, 5]} />
                      <Tooltip />
                      <Bar dataKey="confidence" fill="#22C55E" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </AppShell>
  );
}
